const MAX_METADATA_BYTES = 128 * 1024;
const GITHUB_PULL_REQUEST_URL_PREFIX = 'https://github.com/camaradesuk/syrf/pull/';

export interface PreviewPullRequestMetadata {
  title: string | null;
  description: string | null;
  url: string | null;
}

/**
 * Decodes preview metadata injected by the trusted preview workflow.
 *
 * Pull-request title and description are still untrusted user input. The title
 * must be rendered as text. The description may only be rendered through
 * `MarkdownPipe`, which sanitises the generated HTML with DOMPurify before the
 * `[innerHTML]` binding runs Angular's own DomSanitizer over it.
 */
export function getPreviewPullRequestMetadata(
  prNumber: string | null | undefined,
  titleBase64: string | null | undefined,
  descriptionBase64: string | null | undefined
): PreviewPullRequestMetadata {
  const isValidPrNumber = /^\d+$/.test(prNumber ?? '');

  return {
    title: decodePlainText(titleBase64),
    description: decodePlainText(descriptionBase64),
    url: isValidPrNumber
      ? `${GITHUB_PULL_REQUEST_URL_PREFIX}${prNumber}`
      : null,
  };
}

function decodePlainText(value: string | null | undefined): string | null {
  if (!value) return null;

  try {
    const binary = atob(value);
    if (binary.length > MAX_METADATA_BYTES) return null;

    // `atob` yields one latin1 code unit per character, so the code point is
    // always a single byte here.
    const bytes = Uint8Array.from(
      binary,
      (character) => character.codePointAt(0) ?? 0
    );
    const text = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
    // Trim only to decide emptiness. Leading spaces make an indented code
    // block and trailing spaces make a hard line break, so the value itself
    // is returned verbatim.
    return text.trim() ? text : null;
  } catch {
    return null;
  }
}
